import React, { useState, useEffect } from 'react';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogTrigger } from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import { Input } from '../../../../components/ui/input';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { format } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from '../../../../lib/axios';
import { useAuth } from '../../../../context/AuthProvider';
import { Trans, useLingui } from '@lingui/react/macro';

type CalendarTimeRow = {
  TIMEREGISTRATIONID: number;
  STARTTIME: string; // "HH:mm:ss.SSS"
  ENDTIME: string;   // "HH:mm:ss.SSS"
  LOCATION: string;  // e.g. "MOVE: ZWEVEGEM ..."
  bDELETE: boolean;
  TRIPTIMEID: number;
};

type Props = {
  trigger?: React.ReactNode;
  date: Date;
  row: CalendarTimeRow;
  onSuccess?: () => void;
};

type FormData = {
  activity: string;     // read-only in UI
  startTime: string;    // "HH:mm"
  endTime: string;      // "HH:mm"
};

const toInputTime = (serverTime: string | null | undefined) => {
  // server "HH:mm:ss.SSS" => "HH:mm"
  if (!serverTime) return '';
  const [hh, mm] = serverTime.split(':');
  return `${hh?.padStart(2, '0')}:${mm?.padStart(2, '0')}`;
};

const isWorkRow = (row: CalendarTimeRow) => row.TIMEREGISTRATIONID > 0 && row.TRIPTIMEID === 0;
const isTripRow = (row: CalendarTimeRow) => row.TRIPTIMEID > 0;

const EditActivity = ({ trigger, date, row, onSuccess }: Props) => {
  const { auth } = useAuth();
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  const { control, handleSubmit, formState: { errors, isValid }, trigger: triggerForm, reset } = useForm<FormData>({
    defaultValues: {
      activity: row.LOCATION,
      startTime: toInputTime(row.STARTTIME),
      endTime: toInputTime(row.ENDTIME),
    },
    mode: 'onChange',
  });

  useEffect(() => {
    // Whenever row changes (or dialog reopens), reset form with fresh values
    if (open) {
      reset({
        activity: row.LOCATION,
        startTime: toInputTime(row.STARTTIME),
        endTime: toInputTime(row.ENDTIME),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  const startTime = useWatch({ control, name: 'startTime' });
  const endTime = useWatch({ control, name: 'endTime' });

  const validateTimes = () => {
    if (startTime && endTime) {
      const start = new Date(`1970-01-01T${startTime}:00`);
      const end = new Date(`1970-01-01T${endTime}:00`);
      return start < end || t`Start tijd moet voor eind tijd liggen`;
    }
    return true;
  };

  useEffect(() => {
    if (startTime && endTime) {
      triggerForm(['startTime', 'endTime']);
    }
  }, [startTime, endTime, triggerForm]);

  const dbid = auth.data?.databases[0].dbid;
  const technicianid = auth.data?.databases[0].technicianid;
  const ymd = format(date, 'yyyyMMdd');
  const ts = format(new Date(), 'yyyyMMddHHmmss');

  // --- UPDATE mutation
  const { mutate: saveUpdate} = useMutation({
    mutationFn: async (data: FormData) => {
      if (isWorkRow(row)) {
        // Update timeregistration by id
        return axios.post(`timeregistration?dbid=${dbid}`, {
          timeregistrations: [{
            tijdsregistratieid: row.TIMEREGISTRATIONID,
            technicianid,
            fromdate: ymd,
            todate: ymd,
            fromtime: data.startTime,
            totime: data.endTime,
            timestamp: ts,
            bdelete: false,
          }],
        });
      } else if (isTripRow(row)) {
        // Update triptime by id
        return axios.post(`triptime?dbid=${dbid}`, {
          triptimes: [{
            triptimeid: row.TRIPTIMEID,
            technicianid,
            date: ymd,
            starttime: data.startTime,
            endtime: data.endTime,
            duration: '0',
            timestamp: ts,
            bdelete: false,
          }],
        });
      } else {
        // Nothing to update
        return Promise.resolve();
      }
    },
    onError: () => {
      toast.error(t`Er is een fout opgetreden bij het opslaan van de activiteit.`, { id: 'edit-activity' });
    },
    onSuccess: () => {
      toast.success(t`De activiteit is bijgewerkt.`, { id: 'edit-activity' });
      onSuccess && onSuccess();
      setOpen(false);
    },
  });

  const onSubmit = (data: FormData) => {
    saveUpdate(data);
  };

  const readOnlyActivity = row.LOCATION || '-';

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {trigger && (
        <AlertDialogTrigger asChild>
          {trigger}
        </AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label><Trans>Activiteit</Trans></Label>
            {/* Keep read-only for now; switching requires more data (intervention id, etc.) */}
            <Input value={readOnlyActivity} readOnly />
          </div>

          <div>
            <Label><Trans>Start tijd</Trans><span className="ml-1 text-red-600">*</span></Label>
            <Controller
              name="startTime"
              control={control}
              rules={{ required: t`Start tijd is verplicht`, validate: validateTimes }}
              render={({ field }) => <Input {...field} type="time" />}
            />
            {errors.startTime && <p className="text-sm text-red-500">{errors.startTime.message}</p>}
          </div>

          <div>
            <Label><Trans>Eind tijd</Trans><span className="ml-1 text-red-600">*</span></Label>
            <Controller
              name="endTime"
              control={control}
              rules={{ required: t`Eind tijd is verplicht`, validate: validateTimes }}
              render={({ field }) => <Input {...field} type="time" />}
            />
            {errors.endTime && <p className="text-sm text-red-500">{errors.endTime.message}</p>}
          </div>

          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>
            <Button type="submit" disabled={!isValid}>
              <Trans>Opslaan</Trans>
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default EditActivity;
